import type { ChatCompletionUsage, FinishReason } from "@perceptron/api/apiContracts/chatCompletions";

import type { ToolFamily } from "@demo/shared/types";
import type { MediaData } from "@demo/types/base";

/**
 * Dev fixtures: a canned run the demo replays in place of the model or the Agent harness.
 *
 * Visit the compose page with `?perceptron-test-fixture=<key>`: the composer is filled from the
 * fixture and its run settings applied. Pressing Run (or `autoRun`) sends the normal stream request
 * with `fixture: <key>`, and the server streams the fixture's recorded upstream response through
 * the real relay, so everything from the request onward — relay, client reader, stores, rendering —
 * is exercised. Files live in `public/demo/assets/dev-fixtures/`: `<key>.json` (a developer's own, gitignored)
 * wins over `<key>.example.json` (tracked). Development only.
 */

export const DEV_FIXTURE_QUERY_PARAM = "perceptron-test-fixture";

/** Keys name a file, so they are plain: no path separators, no leading dot. */
export const DEV_FIXTURE_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

/**
 * One frame of a recorded upstream stream (the model's, or the Agent harness's), with the
 * milliseconds that precede it. Model chunks keep only what varies; the replay adds the envelope.
 */
export interface RecordedFrame {
  ms: number;
  /** A chat-completion delta (`reasoning_content`, `content`, `role`). */
  delta?: Record<string, unknown>;
  finish?: FinishReason;
  usage?: ChatCompletionUsage;
  /** A named event (`tool_call_started`, …, `error`) and its payload. */
  event?: string;
  data?: unknown;
  /** The `[DONE]` sentinel. */
  done?: boolean;
}

/** A prompt media item: a full `MediaData`, or one of the demo's template assets by name. */
export type DevFixtureMedia = MediaData | { template: string };

export interface DevFixture {
  /** Human-readable label, only used in dev console output. */
  label: string;
  /** Prompt text for the composer. `\n` splits into paragraphs. */
  userInput?: string;
  /** Prompt media for the composer; a run fills the output carousel from them. */
  userMedia?: DevFixtureMedia[];
  /** Force run toggles for the session, named as the UI labels them. */
  runSettings?: {
    enableThinking?: boolean;
    /** The tool families to turn on (`focus`, `search`); the others are turned off. */
    tools?: ToolFamily[];
  };
  /** Run as soon as the composer is filled. Off by default: the developer presses Run. */
  autoRun?: boolean;

  /**
   * The response, in one of two forms. Shorthand: `reasoning` and `modelResponse` text, streamed
   * in `streaming.tokenSize` chunks every `streaming.intervalMs` (instantly without `streaming`),
   * ending with `finishReason` (default `stop`). Full: `frames`, a recorded stream that can carry
   * anything an upstream sends — tool-call events, usage, errors, any finish reason.
   */
  reasoning?: string;
  modelResponse?: string;
  streaming?: { tokenSize?: number; intervalMs?: number };
  finishReason?: FinishReason;
  frames?: RecordedFrame[];
  /** Chat-completion `id` for replayed chunks; defaults to `fixture-<key>`. */
  id?: string;
}

const DEFAULT_TOKEN_SIZE = 4;
const DEFAULT_TOKEN_INTERVAL_MS = 30;

/** The frames a fixture replays: its own, or ones built from the shorthand. */
export function fixtureFrames(fixture: DevFixture): RecordedFrame[] {
  if (fixture.frames) return fixture.frames;

  const size = fixture.streaming?.tokenSize ?? DEFAULT_TOKEN_SIZE;
  const ms = fixture.streaming ? (fixture.streaming.intervalMs ?? DEFAULT_TOKEN_INTERVAL_MS) : 0;
  const tokens = (text: string | undefined) => {
    const chunks: string[] = [];
    for (let i = 0; i < (text?.length ?? 0); i += size) chunks.push(text!.slice(i, i + size));
    return chunks;
  };

  return [
    { ms: 0, delta: { role: "assistant" } },
    ...tokens(fixture.reasoning).map((token) => ({ ms, delta: { reasoning_content: token } })),
    ...tokens(fixture.modelResponse).map((token) => ({ ms, delta: { content: token } })),
    { ms, finish: fixture.finishReason ?? "stop" },
    { ms: 0, done: true },
  ];
}
